import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from '../store'
import {
  RESTYPE_ADDITION,
  RESTYPE_INTEREST,
  RESTYPE_MONTHS,
  RESTYPE_TARGET,
} from '../store/modules/financialCalc'
import { commify, fixedNumber, units } from '../js/utils'
import '../assets/css/libs.css'
import '../assets/css/style.css'

interface CalcResultProps {
  caseType: 'target' | 'interest' | 'months' | 'addition'
}

function formatAmount(amount: number) {
  if (amount > units.trillionYen.value) {
    return {
      val: `${commify(fixedNumber(amount / units.trillionYen.value, 0))}`,
      unit: `${units.trillionYen.str}`,
    }
  } else if (amount > units.hundredMillionYen.value) {
    return {
      val: `${commify(fixedNumber(amount / units.hundredMillionYen.value, 1))}`,
      unit: `${units.hundredMillionYen.str}`,
    }
  } else if (amount > units.tenThousandYen.value) {
    return {
      val: `${commify(fixedNumber(amount / units.tenThousandYen.value, 0))}`,
      unit: `${units.tenThousandYen.str}`,
    }
  } else {
    return {
      val: `${commify(fixedNumber(amount, 0))}`,
      unit: `${units.yen.str}`,
    }
  }
}

function formatInterest(interest: number) {
  return `${fixedNumber(interest, 2)}`
}

function formatMonths(months: number) {
  const year = Math.floor(months / 12)
  const month = months % 12
  return { year: `${year}`, month: `${month}` }
}

function formatAddition(addition: number) {
  if (addition > units.trillionYen.value) {
    return {
      val: `${commify(fixedNumber(addition / units.trillionYen.value, 0))}`,
      unit: `${units.trillionYen.str}`,
    }
  } else if (addition > 1000000000) {
    // 10億以上で億円表示
    return {
      val: `${commify(fixedNumber(addition / units.hundredMillionYen.value, 1))}`,
      unit: `${units.hundredMillionYen.str}`,
    }
  } else if (addition > 100000) {
    // 10万円以上で万円表示
    return {
      val: `${commify(fixedNumber(addition / units.tenThousandYen.value, 0))}`,
      unit: `${units.tenThousandYen.str}`,
    }
  } else {
    return {
      val: `${commify(fixedNumber(addition, 0))}`,
      unit: `${units.yen.str}`,
    }
  }
}

export const CalcResultComponent: React.FC<CalcResultProps> = (props) => {
  const result = useSelector((state: AppState) => state.financialCalc.result[props.caseType])

  const formatAmountStr = useMemo(() => {
    if (result) {
      return {
        ...formatAmount(result.amount),
        deposit: formatAmount(result.depositAmount),
      }
    } else {
      return {
        val: '- - - - -',
        unit: '万円',
        deposit: { val: '- - - - -', unit: '万円' },
      }
    }
  }, [result])

  const formatMonthsStr = useMemo(() => {
    if (result) {
      return {
        ...formatMonths(result.months),
        deposit: formatMonths(result.depositMonths),
      }
    } else {
      return {
        year: '- - - - -',
        month: '- - - - -',
        deposit: { year: '- - - - -', month: '- - - - -' },
      }
    }
  }, [result])

  const formatAdditionStr = useMemo(() => {
    if (result) {
      return {
        ...formatAddition(result.addition),
        deposit: formatAddition(result.depositAddition),
      }
    } else {
      return {
        val: '- - - - -',
        unit: '万円',
        deposit: { val: '- - - - -', unit: '万円' },
      }
    }
  }, [result])

  return (
    <div className="c-grid--justify-content-center c-form--result">
      {props.caseType === RESTYPE_TARGET && (
        <React.Fragment>
          <div className="c-grid__col">
            <div className="c-form-group u-mr0">
              <span className="c-form-group__sup">投資した場合の評価額</span>
              <span className="c-form-group__input">
                <input
                  type="text"
                  placeholder="- - - - -"
                  className="c-form__output u-size--w222px"
                  readOnly
                  value={formatAmountStr.val}
                />
                <span className="c-form-group__text--large">{formatAmountStr.unit}</span>
              </span>
            </div>
          </div>
          <div className="c-grid__col u-ml10em">
            <div className="c-form-group u-mr0">
              <span className="c-form-group__sup">預金額</span>
              <span className="c-form-group__input">
                <input
                  type="text"
                  placeholder="- - - - -"
                  className="c-form__output u-size--w222px"
                  readOnly
                  value={formatAmountStr.deposit.val}
                />
                <span className="c-form-group__text--large">{formatAmountStr.deposit.unit}</span>
              </span>
            </div>
          </div>
        </React.Fragment>
      )}
      {props.caseType === RESTYPE_INTEREST && (
        <div className="c-grid__col">
          <div className="c-form-group u-mr0">
            <span className="c-form-group__sup">必要な利回り</span>
            <span className="c-form-group__input">
              <input
                type="text"
                placeholder="- - - - -"
                className="c-form__output u-size--w222px"
                readOnly
                value={result ? formatInterest(result.interest) : '- - - - -'}
              />
              <span className="c-form-group__text--large">％</span>
            </span>
          </div>
        </div>
      )}
      {props.caseType === RESTYPE_MONTHS && (
        <React.Fragment>
          <div className="c-grid__col">
            <div className="c-form-group u-mr0">
              <span className="c-form-group__sup">つみたて期間</span>
              <span className="c-form-group__input">
                <input
                  type="text"
                  placeholder="- - - - -"
                  className="c-form__output u-size--w120px"
                  readOnly
                  value={formatMonthsStr.year}
                />
                <span className="c-form-group__text--large">年</span>
                <input
                  type="text"
                  placeholder="- - - - -"
                  className="c-form__output u-size--w120px"
                  readOnly
                  value={formatMonthsStr.month}
                />
                <span className="c-form-group__text--large">ヶ月</span>
              </span>
            </div>
          </div>
          <div className="c-grid__col u-ml10em">
            <div className="c-form-group u-mr0">
              <span className="c-form-group__sup">預金期間</span>
              <span className="c-form-group__input">
                <input
                  type="text"
                  placeholder="- - - - -"
                  className="c-form__output u-size--w120px"
                  readOnly
                  value={formatMonthsStr.deposit.year}
                />
                <span className="c-form-group__text--large">年</span>
                <input
                  type="text"
                  placeholder="- - - - -"
                  className="c-form__output u-size--w120px"
                  readOnly
                  value={formatMonthsStr.deposit.month}
                />
                <span className="c-form-group__text--large">ヶ月</span>
              </span>
            </div>
          </div>
        </React.Fragment>
      )}
      {props.caseType === RESTYPE_ADDITION && (
        <React.Fragment>
          <div className="c-grid__col">
            <div className="c-form-group u-mr0">
              <span className="c-form-group__sup">毎月の積立額</span>
              <span className="c-form-group__input">
                <input
                  type="text"
                  placeholder="- - - - -"
                  className="c-form__output u-size--w222px"
                  readOnly
                  value={formatAdditionStr.val}
                />
                <span className="c-form-group__text--large">{formatAdditionStr.unit}</span>
              </span>
            </div>
          </div>
          <div className="c-grid__col u-ml10em">
            <div className="c-form-group u-mr0">
              <span className="c-form-group__sup">毎月の預金額</span>
              <span className="c-form-group__input">
                <input
                  type="text"
                  placeholder="- - - - -"
                  className="c-form__output u-size--w222px"
                  readOnly
                  value={formatAdditionStr.deposit.val}
                />
                <span className="c-form-group__text--large">{formatAdditionStr.deposit.unit}</span>
              </span>
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  )
}
